import { ArtistJobs } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import {
  useArtistProfileWorkHistoryMutation,
  useArtistProfileWorkHistoryQuery,
  useArtistProfileWorkHistoryStatusMutation
} from '@/features/artist/portfolio/api';
import { validateAddPortfolioItemForm } from '@/features/artist/portfolio/constants/validateAddPortfolioItemForm';

export const useAddPortfolioItemForm = ({
  onSuccess
}: {
  onSuccess: () => void;
}) => {
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const queryClient = useQueryClient();
  const workHistory = useArtistProfileWorkHistoryQuery({ profileId });
  const workHistoryStatus = useArtistProfileWorkHistoryStatusMutation();
  const sendWorkHistory = useArtistProfileWorkHistoryMutation({
    options: {
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({
          queryKey: ['ArtistProfileWorkHistory', profileId]
        });
      }
    }
  });

  const form = useForm<{
    clubName: string;
    clubCountry: string;
    startDate: string | null;
    endDate: string | null;
    untilNow: boolean;
    jobRole: ArtistJobs;
  }>({
    validateInputOnBlur: true,
    initialValues: {
      clubName: '',
      clubCountry: '',
      startDate: null,
      endDate: null,
      untilNow: false,
      jobRole: ArtistJobs.GoGo
    },
    validate: zodResolver(validateAddPortfolioItemForm)
  });

  const formatData = (value: string) => dayjs(value).format('YYYY-MM-DD');

  const excludeDate = (date: Date) =>
    dayjs(date).isBefore(form.getValues().startDate, 'day');

  const onSubmit = form.onSubmit(async (values) => {
    if (
      workHistory.data?.data &&
      values.startDate !== null &&
      values.endDate === null
    ) {
      return sendWorkHistory.mutateAsync({
        params: {
          profileId,
          workHistory: {
            hasWorkingExperience: true,
            workHistory: [
              ...workHistory.data.data,
              {
                ...values,
                startDate: formatData(values.startDate),
                endDate: null
              }
            ]
          }
        }
      });
    }
    if (
      workHistory.data?.data &&
      values.endDate !== null &&
      values.startDate !== null
    ) {
      await sendWorkHistory.mutateAsync({
        params: {
          profileId,
          workHistory: {
            hasWorkingExperience: true,
            workHistory: [
              ...workHistory.data.data,
              {
                ...values,
                startDate: formatData(values.startDate),
                endDate: formatData(values.endDate)
              }
            ]
          }
        }
      });
    }
    await workHistoryStatus.mutateAsync({
      params: {
        profileId,
        workHistory: {
          hasWorkingExperience: true
        }
      }
    });
  });

  const setUntilNow = () => {
    form.setFieldValue('endDate', null);
  };
  useEffect(() => {
    setUntilNow();
  }, [form.getValues().untilNow]);

  return {
    state: {
      form
    },
    functions: {
      onSubmit,
      setUntilNow,
      excludeDate
    }
  };
};
